import type { Priority, SlaPolicy, SlaState } from './types';

export type BusinessHours = { tz: string; days: number[]; start: string; end: string };

function hm(s: string): [number, number] {
  const [h, m] = s.split(':').map(Number);
  return [h ?? 0, m ?? 0];
}

/** Adds working minutes inside the business window (local time; tz applied by the caller's environment in v1). */
export function addBusinessMinutes(start: Date, minutes: number, hours: BusinessHours): Date {
  const [oh, om] = hm(hours.start);
  const [ch, cm] = hm(hours.end);
  let cursor = new Date(start);
  let remaining = minutes;
  for (let guard = 0; guard < 366; guard++) {
    const open = new Date(cursor);
    open.setHours(oh, om, 0, 0);
    const close = new Date(cursor);
    close.setHours(ch, cm, 0, 0);
    const isWorkday = hours.days.includes(cursor.getDay());
    if (isWorkday && cursor < close) {
      if (cursor < open) cursor = open;
      const available = (close.getTime() - cursor.getTime()) / 60000;
      if (remaining <= available) return new Date(cursor.getTime() + remaining * 60000);
      remaining -= available;
    }
    cursor = new Date(cursor);
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(oh, om, 0, 0);
  }
  return cursor;
}

export function computeSlaDues(policy: SlaPolicy, priority: Priority, from: Date): { responseDue: Date; resolveDue: Date } {
  const t = policy.targets[priority];
  if (policy.businessHours === '24x7') {
    return {
      responseDue: new Date(from.getTime() + t.responseMins * 60000),
      resolveDue: new Date(from.getTime() + t.resolveMins * 60000),
    };
  }
  return {
    responseDue: addBusinessMinutes(from, t.responseMins, policy.businessHours),
    resolveDue: addBusinessMinutes(from, t.resolveMins, policy.businessHours),
  };
}

export type SlaStatus = 'ok' | 'at-risk' | 'breached' | 'paused' | 'met';

/** Status of the resolution clock; pass `resolved=true` once the record is Resolved/Closed. */
export function slaStatus(
  sla: Pick<SlaState, 'resolveDue' | 'paused' | 'breached' | 'respondedAt'>,
  now: Date,
  startedAt: Date,
  resolved = false,
): SlaStatus {
  if (resolved) return 'met';
  if (sla.paused) return 'paused';
  if (!sla.resolveDue) return 'ok';
  const due = sla.resolveDue.toMillis();
  if (now.getTime() > due || sla.breached) return 'breached';
  const total = due - startedAt.getTime();
  const elapsed = now.getTime() - startedAt.getTime();
  return total > 0 && elapsed / total >= 0.75 ? 'at-risk' : 'ok';
}
